// eslint-disable-next-line max-classes-per-file
import { VintomErrorCode } from "src/api/model/error/VintomErrorCode";

export type ScenarioElementEndReason = "SEEKED" | "PAUSED" | "FINISHED" | "CLOSED";
export type ScenarioElementProgress = 25 | 50 | 75 | 100;

export abstract class BaseAnalyticsEvent<T extends string> {
  type: T;
  timestamp = Date.now();
  constructor(
    public projectCode: string,
    public groupId: number,
    public scenarioElementId: string,
    public videoCode: string,
    public userIdentifier: string,
    public videoDate: number,
  ) {}
}

export class PlayerInitEvent extends BaseAnalyticsEvent<"PLAYER_INIT"> {
  type: "PLAYER_INIT" = "PLAYER_INIT";
}

export class PlayerStartEvent extends BaseAnalyticsEvent<"PLAYER_START"> {
  type: "PLAYER_START" = "PLAYER_START";
}

export class PlayerErrorEvent extends BaseAnalyticsEvent<"PLAYER_ERROR"> {
  type: "PLAYER_ERROR" = "PLAYER_ERROR";
  constructor(
    projectCode: string,
    groupId: number,
    scenarioElementId: string,
    videoCode: string,
    userIdentifier: string,
    videoDate: number,
    public errorCode: VintomErrorCode,
    public errorMessage: string,
  ) {
    super(projectCode, groupId, scenarioElementId, videoCode, userIdentifier, videoDate);
  }
}

export class ScenarioElementStartEvent extends BaseAnalyticsEvent<"SCENARIO_ELEMENT_START"> {
  type: "SCENARIO_ELEMENT_START" = "SCENARIO_ELEMENT_START";
  currentTime = 0;

  constructor(
    projectCode: string,
    groupId: number,
    scenarioElementId: string,
    videoCode: string,
    userIdentifier: string,
    videoDate: number,
    public videoType: string,
    public volume: number,
    public duration: number,
  ) {
    super(projectCode, groupId, scenarioElementId, videoCode, userIdentifier, videoDate);
  }
}

export class ScenarioElementPlayingEvent extends BaseAnalyticsEvent<"SCENARIO_ELEMENT_PLAYING"> {
  type: "SCENARIO_ELEMENT_PLAYING" = "SCENARIO_ELEMENT_PLAYING";
  constructor(
    projectCode: string,
    groupId: number,
    scenarioElementId: string,
    videoCode: string,
    userIdentifier: string,
    videoDate: number,
    public videoType: string,
    public volume: number,
    public duration: number,
    public currentTime: number,
    public startTime: number,
    public endReason?: ScenarioElementEndReason,
  ) {
    super(projectCode, groupId, scenarioElementId, videoCode, userIdentifier, videoDate);
  }
}

export class ScenarioElementProgressEvent extends BaseAnalyticsEvent<"SCENARIO_ELEMENT_PROGRESS"> {
  type: "SCENARIO_ELEMENT_PROGRESS" = "SCENARIO_ELEMENT_PROGRESS";
  constructor(
    projectCode: string,
    groupId: number,
    scenarioElementId: string,
    videoCode: string,
    userIdentifier: string,
    videoDate: number,
    public progress: ScenarioElementProgress,
  ) {
    super(projectCode, groupId, scenarioElementId, videoCode, userIdentifier, videoDate);
  }
}

export class CTAShowEvent extends BaseAnalyticsEvent<"CTA_SHOW"> {
  type: "CTA_SHOW" = "CTA_SHOW";
  constructor(
    projectCode: string,
    groupId: number,
    scenarioElementId: string,
    videoCode: string,
    userIdentifier: string,
    videoDate: number,
    public ctaId: number,
  ) {
    super(projectCode, groupId, scenarioElementId, videoCode, userIdentifier, videoDate);
  }
}

export class CTAActionEvent extends BaseAnalyticsEvent<"CTA_ACTION"> {
  type: "CTA_ACTION" = "CTA_ACTION";
  constructor(
    projectCode: string,
    groupId: number,
    scenarioElementId: string,
    videoCode: string,
    userIdentifier: string,
    videoDate: number,
    public ctaId: number,
    public actionId: number,
    public actionAdditionalData: object,
  ) {
    super(projectCode, groupId, scenarioElementId, videoCode, userIdentifier, videoDate);
  }
}

export class NotSupportedBrowserEvent extends BaseAnalyticsEvent<"NOT_SUPPORTED_BROWSER"> {
  type: "NOT_SUPPORTED_BROWSER" = "NOT_SUPPORTED_BROWSER";
  constructor(
    projectCode: string,
    groupId: number,
    scenarioElementId: string,
    videoCode: string,
    userIdentifier: string,
    videoDate: number,
  ) {
    super(projectCode, groupId, scenarioElementId, videoCode, userIdentifier, videoDate);
  }
}

export type AnalyticsEvent =
  | PlayerInitEvent
  | PlayerStartEvent
  | PlayerErrorEvent
  | ScenarioElementStartEvent
  | ScenarioElementPlayingEvent
  | ScenarioElementProgressEvent
  | CTAShowEvent
  | CTAActionEvent
  | NotSupportedBrowserEvent;

// tslint:enable:max-classes-per-file
